/**
 * EnterpriseContractForm.tsx — Modal form for creating / editing enterprise contracts
 *
 * Purpose:
 *   Renders a modal overlay form with fields for all enterprise contract fields.
 *   Submits POST (create) or PUT (edit) to /admin/enterprise/contracts.
 *   Closes the modal on success and calls onSuccess() so the parent can refresh.
 *
 * @module packages/billing-admin/src/components/enterprise/EnterpriseContractForm
 * @since 2026-06-03 (AT-50.03 — Revenue Dashboard & Enterprise Screens)
 */
import React, { useState } from 'react';
import {
  createContract,
  updateContract,
  type EnterpriseContract,
} from '../../hooks/useAdminApi50';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import SlidePanel from '../SlidePanel';

/** Fields managed by the form (excludes server-assigned fields). */
type ContractFormData = {
  customer_id: string;
  po_number: string;
  contract_value: number;
  billing_interval: 'monthly' | 'quarterly' | 'annual';
  start_date: string;
  end_date: string;
};

interface EnterpriseContractFormProps {
  /** Existing contract for edit mode; undefined for create mode. */
  contract?: EnterpriseContract;
  /** Called after a successful create or update so the parent can refresh. */
  onSuccess: () => void;
  /** Called when the user dismisses the modal without saving. */
  onClose: () => void;
}

const EMPTY_FORM: ContractFormData = {
  customer_id: '',
  po_number: '',
  contract_value: 0,
  billing_interval: 'monthly',
  start_date: '',
  end_date: '',
};

/**
 * EnterpriseContractForm — modal form for create / edit.
 *
 * @since 2026-06-03 (AT-50.03)
 */
export default function EnterpriseContractForm({
  contract,
  onSuccess,
  onClose,
}: EnterpriseContractFormProps) {
  const { token } = useAdminAuth();
  const isEdit = contract !== undefined;

  const [formData, setFormData] = useState<ContractFormData>(
    contract
      ? {
          customer_id: contract.customer_id,
          po_number: contract.po_number,
          contract_value: contract.contract_value,
          billing_interval: contract.billing_interval,
          start_date: contract.start_date,
          end_date: contract.end_date,
        }
      : EMPTY_FORM,
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleChange(
    field: keyof ContractFormData,
    value: string | number,
  ) {
    setFormData(prev => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!token) return;
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && contract) {
        await updateContract(contract.id, formData, token);
      } else {
        await createContract(formData, token);
      }
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unexpected error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <SlidePanel open onClose={onClose} title={isEdit ? 'Edit Contract' : 'New Contract'} subtitle="Enterprise agreement" size="md">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="customer_id" className="block text-sm font-medium text-gray-700 mb-1">
              Customer ID
            </label>
            <input
              id="customer_id"
              type="text"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={formData.customer_id}
              onChange={e => handleChange('customer_id', e.target.value)}
              required
            />
          </div>

          <div>
            <label htmlFor="po_number" className="block text-sm font-medium text-gray-700 mb-1">
              PO Number
            </label>
            <input
              id="po_number"
              type="text"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={formData.po_number}
              onChange={e => handleChange('po_number', e.target.value)}
              required
            />
          </div>

          <div>
            <label htmlFor="contract_value" className="block text-sm font-medium text-gray-700 mb-1">
              Contract Value ($)
            </label>
            <input
              id="contract_value"
              type="number"
              min={0}
              step={0.01}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={formData.contract_value}
              onChange={e => handleChange('contract_value', parseFloat(e.target.value))}
              required
            />
          </div>

          <div>
            <label htmlFor="billing_interval" className="block text-sm font-medium text-gray-700 mb-1">
              Billing Interval
            </label>
            <select
              id="billing_interval"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={formData.billing_interval}
              onChange={e =>
                handleChange(
                  'billing_interval',
                  e.target.value as ContractFormData['billing_interval'],
                )
              }
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="annual">Annual</option>
            </select>
          </div>

          <div>
            <label htmlFor="start_date" className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              id="start_date"
              type="date"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={formData.start_date}
              onChange={e => handleChange('start_date', e.target.value)}
              required
            />
          </div>

          <div>
            <label htmlFor="end_date" className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              id="end_date"
              type="date"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={formData.end_date}
              onChange={e => handleChange('end_date', e.target.value)}
              required
            />
          </div>

          {error && <p className="text-red-500 text-sm">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Contract'}
            </button>
          </div>
        </form>
    </SlidePanel>
  );
}
